<template>
  <div class="models">
    <div class="models-list">
      <div class="view-toggle">
        <label class="toggle-label">Editor weergave:</label>
        <select v-model="selectedEditor" class="toggle-select">
          <option value="old">Oud</option>
          <option value="new">Nieuw (horizontaal)</option>
        </select>
      </div>
      <template v-for="(categoryGroups, categoryName) in categorizedGroups" :key="categoryName">
        <div class="category-container" :class="`category-${categoryName.toLowerCase()}`">
          <div class="category-header">
            <h1>{{ categoryName }}</h1>
          </div>
          
          <template v-for="(group, groupKey) in categoryGroups" :key="groupKey">
            <div class="group-header">
              <h2>{{ group.type }}</h2>
              <span class="group-count">{{ group.models.length }} model{{ group.models.length !== 1 ? 's' : '' }}</span>
            </div>
            
            <div class="group-cards">
              <router-link :to="editorLink(model)" v-for="(model, i) in group.models" :key="model.id" class="link">
                <div class="card">
                  <div class="card-image">
                    <img :src="`${previewsServer + modelsLocation}previews/models/${model.name}.png`" alt="">
                  </div>

                  <div class="card-info">
                    <h3>{{ model.name }}</h3>
                    <span>type: {{ model.type.name }}</span>
                    <span>categorie: {{ model.category.name }}</span>
                  </div>
                </div>
              </router-link>
            </div>
          </template>
        </div>
      </template>

      <div class="models-list-new">
        <LoadingButton @click="createNewModel" >Nieuw model</LoadingButton>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import LoadingButton from '@/components/inputs/buttons/LoadingButton.vue'

  export default {
    name: "ModelsOverview",

    components: {
      LoadingButton
    },

    data () {
      return {
        modelsLocation: import.meta.env.VITE_VUE_APP_MODELS_LOCATION,
        previewsServer: import.meta.env.VITE_VUE_APP_PREVIEWS_SERVER,
        selectedEditor: localStorage.getItem('cmsEditorView') || 'new',
      }
    },

    methods: {
      async get () {
        await this.$store.dispatch('getModelsInfo')
      },
      editorLink(model) {
        const base = this.selectedEditor === 'new' ? '/cms/models-v02' : '/cms/models'
        return `${base}/${model.id}`
      },
      createNewModel() {
        const base = this.selectedEditor === 'new' ? '/cms/models-v02' : '/cms/models'
        this.$router.push(`${base}/new`)
      }
    },

    computed: {
      ...mapGetters({
        models: "getModels"
      }),
      
      sortedModels() {
        // Check of models bestaat en een array is
        if (!this.models || !Array.isArray(this.models)) {
          return [];
        }

        // Definieer de gewenste volgorde voor types (gebruik exacte namen uit database!)
        const typeOrder = ['Plat dak', 'Aanbouwveranda', 'Kapschuur', 'Zadeldak'];
        const categoryOrder = ['Exclusive', 'Basic'];
        
        return [...this.models].sort((a, b) => {
          // 1. Sorteer EERST op type (Plat dak bovenaan)
          const typeA = a.type?.name || '';
          const typeB = b.type?.name || '';
          
          // Vind de index in onze gewenste volgorde
          let typeIndexA = typeOrder.findIndex(type => type === typeA);
          let typeIndexB = typeOrder.findIndex(type => type === typeB);
          
          // Als niet gevonden, zet onderaan (hoge waarde)
          if (typeIndexA === -1) typeIndexA = 999;
          if (typeIndexB === -1) typeIndexB = 999;
          
          if (typeIndexA !== typeIndexB) {
            return typeIndexA - typeIndexB;
          }
          
          // 2. Als types gelijk zijn, sorteer op categorie
          const categoryA = a.category?.name || '';
          const categoryB = b.category?.name || '';
          
          let categoryIndexA = categoryOrder.findIndex(cat => cat === categoryA);
          let categoryIndexB = categoryOrder.findIndex(cat => cat === categoryB);
          
          if (categoryIndexA === -1) categoryIndexA = 999;
          if (categoryIndexB === -1) categoryIndexB = 999;
          
          if (categoryIndexA !== categoryIndexB) {
            return categoryIndexA - categoryIndexB;
          }
          
          // 3. Als beide gelijk zijn, sorteer alfabetisch op naam
          return a.name.localeCompare(b.name, 'nl', { sensitivity: 'base' });
        });
      },
      
      groupedModels() {
        const groups = {};
        const typeOrder = ['Plat dak', 'Aanbouwveranda', 'Kapschuur', 'Zadeldak'];
        const categoryOrder = ['Exclusive', 'Basic'];
        
        this.sortedModels.forEach(model => {
          const category = model.category?.name || 'Onbekend';
          const type = model.type?.name || 'Onbekend';
          
          // Vind categorie index voor sortering (Exclusive krijgt 0, Basic krijgt 1)
          let categoryIndex = categoryOrder.findIndex(c => c === category);
          if (categoryIndex === -1) categoryIndex = 999;
          
          // Vind type index voor sortering binnen categorie (Plat dak krijgt 0, etc.)
          let typeIndex = typeOrder.findIndex(t => t === type);
          if (typeIndex === -1) typeIndex = 999;
          
          // Maak groupKey met categorie eerst, dan type
          const groupKey = `${categoryIndex.toString().padStart(3, '0')}-${typeIndex.toString().padStart(3, '0')}-${category} - ${type}`;
          
          if (!groups[groupKey]) {
            groups[groupKey] = {
              category,
              type,
              models: []
            };
          }
          
          groups[groupKey].models.push(model);
        });
        
        // Sorteer de groepen
        const sortedGroups = {};
        Object.keys(groups).sort().forEach(key => {
          sortedGroups[key] = groups[key];
        });
        
        return sortedGroups;
      },
      
      categorizedGroups() {
        const categories = {};
        
        Object.values(this.groupedModels).forEach(group => {
          const categoryName = group.category;
          
          if (!categories[categoryName]) {
            categories[categoryName] = {};
          }
          
          // Gebruik type als key binnen de categorie
          categories[categoryName][group.type] = group;
        });
        
        return categories;
      }
    },

    created () {
      this.get()
    },
    watch: {
      selectedEditor(newVal) {
        try { localStorage.setItem('cmsEditorView', newVal) } catch(e) {}
      }
    }
  }
</script>

<style lang="scss" scoped>

.models {
  display: flex;
  padding: 20px;
  justify-content: center;

  &-list {
    display: flex;
    flex-direction: column;
    width: min(100%, 1200px);
    gap: 40px;

    &-new {
      display: flex;
      justify-content: center;
      padding: 15px;
      margin-top: 10px;
    }
  }
}

.category-container {
  padding: 20px;
  border-radius: 8px;
  border: 2px solid transparent;
  
  &.category-exclusive {
    background: linear-gradient(135deg, #f8f9ff 0%, #fff5e6 100%);
    border-color: #babc16;
    box-shadow: 0 4px 12px rgba(186, 188, 22, 0.1);
  }
  
  &.category-basic {
    background: linear-gradient(135deg, #f0f8ff 0%, #f5f5f5 100%);
    border-color: #4A5568;
    box-shadow: 0 4px 12px rgba(74, 85, 104, 0.1);
  }
}

.category-header {
  margin-bottom: 25px;
  text-align: center;
  
  h1 {
    margin: 0;
    font-size: 2rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    
    .category-exclusive & {
      color: #babc16;
      text-shadow: 1px 1px 2px rgba(186, 188, 22, 0.2);
    }
    
    .category-basic & {
      color: #4A5568;
      text-shadow: 1px 1px 2px rgba(74, 85, 104, 0.2);
    }
  }
}

.group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 25px 0 12px 0;
  padding-bottom: 6px;
  border-bottom: 2px solid $c-secondary;

  h2 {
    margin: 0;
    color: $c-primary;
    font-size: 1.3rem;
    font-weight: 600;
  }

  &-count {
    color: #666;
    font-size: 0.85rem;
    font-weight: 500;
  }
}

.group-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.card {
  display: flex;
  border-radius: $radius-m-1;
  box-shadow: $shadow-m-0;
  background-color: white;
  overflow: hidden;
  gap: 12px;
  cursor: pointer;
  padding: 10px;
  min-height: 80px;

  &-image {
    background-color: $c-background-1;
    height: 80px;
    width: 80px;
    border-radius: $radius-m-1;
    flex-shrink: 0;

    & img {
      object-fit: contain;
      max-height: 80px;
      max-width: 80px;
      min-height: 80px;
      min-width: 80px;
    }
  }

  &-info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: left;
    gap: 4px;
    flex: 1;

    & h3 {
      margin: 0px;
      font-size: 1.0rem;
      line-height: 1.2;
    }

    & span {
      font-size: 0.8rem;
      color: #666;
      line-height: 1.1;
    }
  }

  &:hover {
    background-color: hsl(0, 0%, 0%, 0.08);
    transform: translateY(-1px);
    box-shadow: $shadow-m-1;
  }
}

.link {
  text-decoration: none;
  color: black;
}

.view-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0 20px 0;
}
.toggle-label { font-weight: 600; }
.toggle-select { padding: 6px 10px; border-radius: 6px; }

</style>